/**
 * Issue #173 — 质检报告聚合 + 训练合格分规则 API 客户端（真实接口）。
 *
 * 全部经 apiClient：强制 response.ok + content-type 校验，错误抛 ApiError。
 */
import { apiClient } from './api';
import { encodeQuery } from './api-client';
import type {
  AiTextAnalysisConfig,
  AiTextAnalysisConfigEnvelope,
  AsrCorrectionConfig,
  AsrCorrectionConfigEnvelope,
  CompliantCallTargetConfigEnvelope,
  KpiPointRulesConfig,
  KpiPointRulesEnvelope,
  LearningTargetMinutesConfigEnvelope,
  MaxBatchUploadConfigEnvelope,
  QcMinDurationConfigEnvelope,
  QcReportColumnsResult,
  QcReportListResult,
  QcReportsListQuery,
  QcScoringRulesConfig,
  QcScoringRulesConfigEnvelope,
  TrainingScoreConfig,
  TrainingScoreConfigEnvelope,
} from './types';

/** Issue #232：最短有效质检时长（KPI考核）读写端点（GET 任意已登录可读 / PATCH 仅 ADMIN+）。 */
export const QC_MIN_DURATION_CONFIG_PATH = '/system/qc-min-duration-config';

/** Issue #385：质检批量上传最大文件数（语音识别API）读写端点（GET 任意已登录可读 / PATCH 仅 ADMIN+）。 */
export const MAX_BATCH_UPLOAD_CONFIG_PATH = '/system/max-batch-upload-config';

/** Issue #263：合规通话目标数（KPI考核）读写端点（GET 任意已登录可读 / PATCH 仅 ADMIN+）。 */
export const COMPLIANT_CALL_TARGET_CONFIG_PATH = '/system/compliant-call-target-config';

/** Issue #278：学习目标分钟数（KPI考核）读写端点（GET 任意已登录可读 / PATCH 仅 ADMIN+）。 */
export const LEARNING_TARGET_MINUTES_CONFIG_PATH = '/system/learning-target-minutes-config';

/** 质检报告聚合列表（MANAGER+，按 orgId 隔离）。 */
export function listQcReportsApi(query: QcReportsListQuery = {}): Promise<QcReportListResult> {
  return apiClient.get<QcReportListResult>(`/qc/reports${encodeQuery(query as Record<string, unknown>)}`);
}

/** Issue #207：质检报告列表可选列白名单（前端列显示/隐藏面板）。 */
export function listQcReportColumnsApi(): Promise<QcReportColumnsResult> {
  return apiClient.get<QcReportColumnsResult>('/qc/reports/columns');
}

/** 读取训练合格分规则（ADMIN+）。 */
export function getTrainingScoreConfigApi(): Promise<TrainingScoreConfigEnvelope> {
  return apiClient.get<TrainingScoreConfigEnvelope>('/system/training-score-config');
}

/** 保存训练合格分规则（ADMIN+，越界由服务端钳制）。 */
export function updateTrainingScoreConfigApi(
  body: Partial<TrainingScoreConfig>,
): Promise<TrainingScoreConfigEnvelope> {
  return apiClient.patch<TrainingScoreConfigEnvelope>('/system/training-score-config', body);
}

/** Issue #227：读取 AI 文本分析字数配置（ADMIN+，按 orgId 隔离）。 */
export function getAiTextAnalysisConfigApi(): Promise<AiTextAnalysisConfigEnvelope> {
  return apiClient.get<AiTextAnalysisConfigEnvelope>('/system/ai-text-config');
}

/** Issue #227：保存 AI 文本分析字数配置（ADMIN+；最大值不得小于最小值，违反返回 400）。 */
export function updateAiTextAnalysisConfigApi(
  body: Partial<AiTextAnalysisConfig>,
): Promise<AiTextAnalysisConfigEnvelope> {
  return apiClient.patch<AiTextAnalysisConfigEnvelope>('/system/ai-text-config', body);
}

/** Issue #291：ASR 文本纠错配置（系统设置 → AI文本分析 → AI文本纠错）读写端点（ADMIN+）。 */
export const ASR_CORRECTION_CONFIG_PATH = '/system/asr-correction-config';

/** Issue #291：读取 ASR 文本纠错配置（ADMIN+，按 orgId 隔离）。 */
export function getAsrCorrectionConfigApi(): Promise<AsrCorrectionConfigEnvelope> {
  return apiClient.get<AsrCorrectionConfigEnvelope>(ASR_CORRECTION_CONFIG_PATH);
}

/** Issue #291：保存 ASR 文本纠错配置（ADMIN+；temperature 最大 0.2，越界返回 400 / 服务端钳制）。 */
export function updateAsrCorrectionConfigApi(
  body: Partial<AsrCorrectionConfig>,
): Promise<AsrCorrectionConfigEnvelope> {
  return apiClient.patch<AsrCorrectionConfigEnvelope>(ASR_CORRECTION_CONFIG_PATH, body);
}

/** Issue #324：AI 评分规则（系统设置 → AI文本分析 → AI评分规则）读写端点（ADMIN+）。 */
export const QC_SCORING_RULES_CONFIG_PATH = '/system/qc-scoring-rules-config';

/** Issue #324：读取 AI 评分规则（ADMIN+，按 orgId 隔离）。 */
export function getQcScoringRulesConfigApi(): Promise<QcScoringRulesConfigEnvelope> {
  return apiClient.get<QcScoringRulesConfigEnvelope>(QC_SCORING_RULES_CONFIG_PATH);
}

/** Issue #324：保存 AI 评分规则（ADMIN+；顺序非法返回明确中文 400）。运行时即时生效。 */
export function updateQcScoringRulesConfigApi(
  body: Partial<QcScoringRulesConfig>,
): Promise<QcScoringRulesConfigEnvelope> {
  return apiClient.patch<QcScoringRulesConfigEnvelope>(QC_SCORING_RULES_CONFIG_PATH, body);
}

/** Issue #272：读取积分规则（ADMIN+，按 orgId 隔离）。 */
export function getKpiPointRulesApi(): Promise<KpiPointRulesEnvelope> {
  return apiClient.get<KpiPointRulesEnvelope>('/system/kpi-point-rules');
}

/** Issue #272：保存积分规则（ADMIN+；越界由服务端归一化钳制）。 */
export function updateKpiPointRulesApi(
  body: Partial<KpiPointRulesConfig>,
): Promise<KpiPointRulesEnvelope> {
  return apiClient.patch<KpiPointRulesEnvelope>('/system/kpi-point-rules', body);
}

/**
 * Issue #232：读取本租户最短有效质检时长（秒）。任意已登录角色可读（质检录音屏据此判定
 * 「无效质检作废」阈值）；返回当前值 + 合法区间 + 出厂默认。
 */
export function getQcMinDurationConfigApi(): Promise<QcMinDurationConfigEnvelope> {
  return apiClient.get<QcMinDurationConfigEnvelope>(QC_MIN_DURATION_CONFIG_PATH);
}

/**
 * Issue #232：保存最短有效质检时长（秒，仅 ADMIN+，按 orgId 隔离）。越界由服务端钳制到
 * [10, 600]。运行时读取即时生效（KPI考核 / 系统设置）。
 */
export function updateQcMinDurationConfigApi(
  minValidDurationSec: number,
): Promise<QcMinDurationConfigEnvelope> {
  return apiClient.patch<QcMinDurationConfigEnvelope>(QC_MIN_DURATION_CONFIG_PATH, {
    minValidDurationSec,
  });
}

/**
 * Issue #385：读取本租户质检批量上传最大文件数。任意已登录角色可读；返回当前值 + 合法区间 + 出厂默认。
 */
export function getMaxBatchUploadConfigApi(): Promise<MaxBatchUploadConfigEnvelope> {
  return apiClient.get<MaxBatchUploadConfigEnvelope>(MAX_BATCH_UPLOAD_CONFIG_PATH);
}

/**
 * Issue #385：保存质检批量上传最大文件数（仅 ADMIN+，按 orgId 隔离）。越界由服务端钳制到
 * [1, 50]。运行时读取即时生效（移动端质检中心批量选择据此限制）。
 */
export function updateMaxBatchUploadConfigApi(
  maxBatchUploadFiles: number,
): Promise<MaxBatchUploadConfigEnvelope> {
  return apiClient.patch<MaxBatchUploadConfigEnvelope>(MAX_BATCH_UPLOAD_CONFIG_PATH, {
    maxBatchUploadFiles,
  });
}

/**
 * Issue #263：读取本租户合规通话目标数。任意已登录角色可读；返回当前值 + 合法区间 + 出厂默认。
 */
export function getCompliantCallTargetConfigApi(): Promise<CompliantCallTargetConfigEnvelope> {
  return apiClient.get<CompliantCallTargetConfigEnvelope>(
    COMPLIANT_CALL_TARGET_CONFIG_PATH,
  );
}

/**
 * Issue #263：保存合规通话目标数（仅 ADMIN+，按 orgId 隔离）。0 / 1001 / 非整数返回 400；
 * 合法值越界由服务端钳制到 [1, 1000]。运行时即时生效，移动端首页「今天目标」随之更新。
 */
export function updateCompliantCallTargetConfigApi(
  target: number,
): Promise<CompliantCallTargetConfigEnvelope> {
  return apiClient.patch<CompliantCallTargetConfigEnvelope>(
    COMPLIANT_CALL_TARGET_CONFIG_PATH,
    { target },
  );
}

/**
 * Issue #278：读取本租户学习目标分钟数。任意已登录角色可读；返回当前值 + 合法区间 + 出厂默认。
 */
export function getLearningTargetMinutesConfigApi(): Promise<LearningTargetMinutesConfigEnvelope> {
  return apiClient.get<LearningTargetMinutesConfigEnvelope>(
    LEARNING_TARGET_MINUTES_CONFIG_PATH,
  );
}

/**
 * Issue #278 / #292：保存学习目标分钟数（仅 ADMIN+，按 orgId 隔离）。小于 1 / 大于 1000 /
 * 非整数返回 400；运行时即时生效，移动端首页「目标 N 分钟」随之更新。默认 5（#292）。
 */
export function updateLearningTargetMinutesConfigApi(
  targetMinutes: number,
): Promise<LearningTargetMinutesConfigEnvelope> {
  return apiClient.patch<LearningTargetMinutesConfigEnvelope>(
    LEARNING_TARGET_MINUTES_CONFIG_PATH,
    { targetMinutes },
  );
}
